'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PrayerGroup {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  avatar_emoji: string;
  goal_prayers_per_day: number;
  is_public: boolean;
  created_at: string;
  member_count?: number;
}

interface GroupMember {
  id: string;
  user_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  user_profiles?: { full_name: string; email: string } | null;
}

interface GroupStreak {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  total_prayers: number;
  last_prayer_date: string | null;
  user_profiles?: { full_name: string } | null;
}

interface GroupNotification {
  id: string;
  group_id: string;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  prayer_groups?: { name: string } | null;
}

interface AISuggestion {
  name: string;
  emoji: string;
  description: string;
  matchReason: string;
  goalPrayersPerDay: number;
  matchScore: number;
}

type ActiveTab = 'my-groups' | 'progress' | 'notifications';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function generateInviteCode(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatBadge({ value, label, color }: { value: number | string; label: string; color: string }) {
  return (
    <div className={`flex flex-col items-center px-4 py-2 rounded-xl ${color}`}>
      <span className="text-xl font-700 text-foreground">{value}</span>
      <span className="text-xs text-muted-foreground mt-0.5">{label}</span>
    </div>
  );
}

function GroupCard({
  group,
  isOwner,
  onSelect,
  onLeave,
}: {
  group: PrayerGroup;
  isOwner: boolean;
  onSelect: (g: PrayerGroup) => void;
  onLeave: (id: string) => void;
}) {
  return (
    <div
      className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3 hover:border-primary/40 transition-all duration-200 cursor-pointer group"
      onClick={() => onSelect(group)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{group.avatar_emoji}</span>
          <div>
            <h3 className="font-600 text-foreground text-base leading-tight">{group.name}</h3>
            {group.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{group.description}</p>
            )}
          </div>
        </div>
        {isOwner && (
          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">Admin</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <StatBadge value={group.member_count ?? 0} label="Members" color="bg-muted" />
        <StatBadge value={group.goal_prayers_per_day} label="Daily Goal" color="bg-muted" />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onLeave(group.id); }}
        className="text-xs text-muted-foreground hover:text-destructive transition-colors self-end"
      >
        Leave group
      </button>
    </div>
  );
}

// ─── AI Suggestion Card ───────────────────────────────────────────────────────
function AISuggestionCard({
  suggestion,
  onCreateFromSuggestion,
}: {
  suggestion: AISuggestion;
  onCreateFromSuggestion: (s: AISuggestion) => void;
}) {
  const scoreColor =
    suggestion.matchScore >= 90
      ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
      : suggestion.matchScore >= 75
      ? 'text-primary bg-primary/10 border-primary/20' :'text-amber-600 bg-amber-50 border-amber-200';

  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-col gap-3 hover:border-primary/40 transition-all duration-200">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="text-2xl">{suggestion.emoji}</span>
          <div>
            <h4 className="font-600 text-foreground text-sm leading-tight">{suggestion.name}</h4>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-500 mt-0.5 ${scoreColor}`}>
              {suggestion.matchScore}% match
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg flex-shrink-0">
          <Icon name="SunIcon" size={12} />
          <span>{suggestion.goalPrayersPerDay}/day</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{suggestion.description}</p>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-primary">
          <Icon name="SparklesIcon" size={12} />
          <span>{suggestion.matchReason}</span>
        </div>
        <button
          onClick={() => onCreateFromSuggestion(suggestion)}
          className="text-xs px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-500 hover:opacity-90 transition-opacity flex-shrink-0"
        >
          Create Group
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PrayerGroupsPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<ActiveTab>('my-groups');
  const [groups, setGroups] = useState<PrayerGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<PrayerGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [streaks, setStreaks] = useState<GroupStreak[]>([]);
  const [notifications, setNotifications] = useState<GroupNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', avatar_emoji: '🕌', goal_prayers_per_day: 5 });
  const [saving, setSaving] = useState(false);

  // AI Suggestions state
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [userProfile, setUserProfile] = useState<{
    streak: number;
    habits: string[];
    prayerSchedule: string;
  }>({ streak: 0, habits: [], prayerSchedule: '' });

  const EMOJIS = ['🕌', '🌙', '⭐', '📿', '🤲', '🕋', '🌟', '🌺', '🌸', '🕊️'];

  // ── Fetch user profile for AI context ────────────────────────────────────
  const fetchUserProfile = useCallback(async () => {
    if (!user) return;
    try {
      const [streakRes, habitsRes, profileRes] = await Promise.all([
        supabase.from('streaks').select('current_streak').eq('user_id', user.id).maybeSingle(),
        supabase.from('habits').select('label').eq('user_id', user.id).eq('is_active', true),
        supabase.from('user_profiles').select('prayer_method, prayer_city').eq('id', user.id).maybeSingle(),
      ]);
      setUserProfile({
        streak: streakRes.data?.current_streak ?? 0,
        habits: (habitsRes.data ?? []).map((h: any) => h.label),
        prayerSchedule: profileRes.data?.prayer_method
          ? `${profileRes.data.prayer_method} method${profileRes.data.prayer_city ? ` in ${profileRes.data.prayer_city}` : ''}`
          : 'Standard 5 daily prayers',
      });
    } catch {
      // silently fail
    }
  }, [user]);

  // ── Fetch AI suggestions ──────────────────────────────────────────────────
  const fetchAISuggestions = useCallback(async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await fetch('/api/ai/prayer-group-suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          streakLevel: userProfile.streak,
          habits: userProfile.habits,
          prayerSchedule: userProfile.prayerSchedule,
          existingGroups: groups.length,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load suggestions');
      setAiSuggestions(data.suggestions ?? []);
    } catch (err: any) {
      setAiError(err.message || 'Could not load suggestions');
    } finally {
      setAiLoading(false);
    }
  }, [userProfile, groups.length]);

  // ── Fetch groups ──────────────────────────────────────────────────────────
  const fetchGroups = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: memberRows } = await supabase
        .from('prayer_group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (!memberRows?.length) { setGroups([]); setLoading(false); return; }

      const groupIds = memberRows.map((r) => r.group_id);
      const { data: groupData } = await supabase
        .from('prayer_groups')
        .select('*')
        .in('id', groupIds);

      if (!groupData) { setGroups([]); setLoading(false); return; }

      const enriched = await Promise.all(
        groupData.map(async (g) => {
          const { count } = await supabase
            .from('prayer_group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', g.id);
          return { ...g, member_count: count ?? 0 };
        })
      );
      setGroups(enriched);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ── Fetch group details ───────────────────────────────────────────────────
  const fetchGroupDetails = useCallback(async (groupId: string) => {
    const [membersResult, streaksResult] = await Promise.all([
      supabase
        .from('prayer_group_members')
        .select('*, user_profiles(full_name, email)')
        .eq('group_id', groupId),
      supabase
        .from('prayer_group_streaks')
        .select('*, user_profiles(full_name)')
        .eq('group_id', groupId)
        .order('current_streak', { ascending: false }),
    ]);
    setMembers(membersResult.data ?? []);
    setStreaks(streaksResult.data ?? []);
  }, []);

  // ── Fetch notifications ───────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('prayer_group_notifications')
      .select('*, prayer_groups(name)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);
    setNotifications(data ?? []);
    setUnreadCount(data?.filter((n) => !n.is_read).length ?? 0);
  }, [user]);

  useEffect(() => {
    fetchGroups();
    fetchNotifications();
    fetchUserProfile();
  }, [fetchGroups, fetchNotifications, fetchUserProfile]);

  useEffect(() => {
    if (selectedGroup) fetchGroupDetails(selectedGroup.id);
  }, [selectedGroup, fetchGroupDetails]);

  // ── Create group ──────────────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    if (!user || !newGroup.name.trim()) return;
    setSaving(true);
    try {
      const { data: group, error } = await supabase
        .from('prayer_groups')
        .insert({ ...newGroup, created_by: user.id })
        .select()
        .single();
      if (error || !group) return;

      await supabase.from('prayer_group_members').insert({
        group_id: group.id,
        user_id: user.id,
        role: 'admin',
      });

      setShowCreateModal(false);
      setNewGroup({ name: '', description: '', avatar_emoji: '🕌', goal_prayers_per_day: 5 });
      fetchGroups();
    } finally {
      setSaving(false);
    }
  };

  // ── Pre-fill create modal from AI suggestion ──────────────────────────────
  const handleCreateFromSuggestion = (suggestion: AISuggestion) => {
    setNewGroup({
      name: suggestion.name,
      description: suggestion.description,
      avatar_emoji: suggestion.emoji,
      goal_prayers_per_day: suggestion.goalPrayersPerDay,
    });
    setShowCreateModal(true);
  };

  // ── Invite ────────────────────────────────────────────────────────────────
  const handleGenerateInvite = async () => {
    if (!user || !selectedGroup) return;
    const code = generateInviteCode();
    await supabase.from('prayer_group_invitations').insert({
      group_id: selectedGroup.id,
      invited_by: user.id,
      invited_email: 'open-invite@rafeeq.app',
      invite_code: code,
    });
    setInviteCode(code);
    setShowInviteModal(true);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(inviteCode).catch(() => {});
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // ── Join group ────────────────────────────────────────────────────────────
  const handleJoinGroup = async () => {
    if (!user || !joinCode.trim()) return;
    setJoinError('');
    setSaving(true);
    try {
      const { data: invite } = await supabase
        .from('prayer_group_invitations')
        .select('*')
        .eq('invite_code', joinCode.trim().toUpperCase())
        .eq('status', 'pending')
        .maybeSingle();

      if (!invite) { setJoinError('Invalid or expired invite code.'); return; }

      const { error } = await supabase.from('prayer_group_members').insert({
        group_id: invite.group_id,
        user_id: user.id,
        role: 'member',
      });

      if (error?.code === '23505') { setJoinError('You are already in this group.'); return; }

      await supabase
        .from('prayer_group_invitations')
        .update({ status: 'accepted' })
        .eq('id', invite.id);

      setShowJoinModal(false);
      setJoinCode('');
      fetchGroups();
    } finally {
      setSaving(false);
    }
  };

  // ── Leave group ───────────────────────────────────────────────────────────
  const handleLeaveGroup = async (groupId: string) => {
    if (!user) return;
    await supabase
      .from('prayer_group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', user.id);
    if (selectedGroup?.id === groupId) setSelectedGroup(null);
    fetchGroups();
  };

  // ── Mark notifications read ───────────────────────────────────────────────
  const markAllRead = async () => {
    if (!user) return;
    await supabase
      .from('prayer_group_notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    fetchNotifications();
  };

  const NOTIF_ICONS: Record<string, string> = {
    streak_milestone: 'FireIcon',
    member_joined: 'UserPlusIcon',
    member_left: 'UserMinusIcon',
    invite_received: 'EnvelopeIcon',
    group_goal_reached: 'TrophyIcon',
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AppLayout activeRoute="/prayer-groups">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-700 text-foreground">Prayer Groups</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Pray together, grow together
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowJoinModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-500 text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="LinkIcon" size={16} />
              Join Group
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity"
            >
              <Icon name="PlusIcon" size={16} />
              New Group
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit">
          {(['my-groups', 'progress', 'notifications'] as ActiveTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative px-4 py-2 rounded-lg text-sm font-500 transition-all duration-150 capitalize ${
                activeTab === tab
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'notifications' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-600 w-4 h-4 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* ── My Groups Tab ── */}
        {activeTab === 'my-groups' && (
          <div className="space-y-6">
            {/* AI Suggestions Section */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Icon name="SparklesIcon" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-600 text-foreground text-sm">AI Group Suggestions</h2>
                    <p className="text-xs text-muted-foreground">
                      Matched to your streak{userProfile.streak > 0 ? ` (${userProfile.streak} days)` : ''}, habits & schedule
                    </p>
                  </div>
                </div>
                <button
                  onClick={fetchAISuggestions}
                  disabled={aiLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-500 hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {aiLoading ? (
                    <>
                      <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Suggesting…
                    </>
                  ) : (
                    <>
                      <Icon name="ArrowPathIcon" size={12} />
                      {aiSuggestions.length > 0 ? 'Refresh' : 'Suggest Groups'}
                    </>
                  )}
                </button>
              </div>

              {aiError && (
                <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-xl px-3 py-2 mb-3">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {aiError}
                </div>
              )}

              {aiSuggestions.length === 0 && !aiLoading && !aiError && (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <span className="text-3xl mb-2">✨</span>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Tap <strong>Suggest Groups</strong> to get AI-powered prayer group recommendations based on your spiritual profile.
                  </p>
                </div>
              )}

              {aiLoading && aiSuggestions.length === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bg-card border border-border rounded-2xl p-4 animate-pulse h-32" />
                  ))}
                </div>
              )}

              {aiSuggestions.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {aiSuggestions.map((s, i) => (
                    <AISuggestionCard
                      key={i}
                      suggestion={s}
                      onCreateFromSuggestion={handleCreateFromSuggestion}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* My Groups Grid */}
            <div>
              <h2 className="font-600 text-foreground text-sm mb-3">My Groups</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading ? (
                  Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse h-36" />
                  ))
                ) : groups.length === 0 ? (
                  <div className="col-span-2 flex flex-col items-center justify-center py-12 text-center">
                    <span className="text-5xl mb-4">🕌</span>
                    <h3 className="font-600 text-foreground text-lg">No groups yet</h3>
                    <p className="text-muted-foreground text-sm mt-1 max-w-xs">
                      Create a prayer group or join one with an invite code to start tracking together.
                    </p>
                  </div>
                ) : (
                  groups.map((g) => (
                    <GroupCard
                      key={g.id}
                      group={g}
                      isOwner={g.created_by === user?.id}
                      onSelect={(grp) => { setSelectedGroup(grp); setActiveTab('progress'); }}
                      onLeave={handleLeaveGroup}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Progress Tab ── */}
        {activeTab === 'progress' && (
          <div className="space-y-5">
            {/* Group selector */}
            {groups.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {groups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setSelectedGroup(g)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-500 border transition-all ${
                      selectedGroup?.id === g.id
                        ? 'bg-primary/10 border-primary/40 text-primary' :'border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    <span>{g.avatar_emoji}</span>
                    {g.name}
                  </button>
                ))}
              </div>
            )}

            {!selectedGroup ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="text-4xl mb-3">📊</span>
                <p className="text-muted-foreground text-sm">Select a group to view progress</p>
              </div>
            ) : (
              <>
                {/* Group header */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{selectedGroup.avatar_emoji}</span>
                      <div>
                        <h2 className="font-700 text-foreground text-lg">{selectedGroup.name}</h2>
                        {selectedGroup.description && (
                          <p className="text-sm text-muted-foreground">{selectedGroup.description}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleGenerateInvite}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-sm font-500 hover:bg-primary/20 transition-colors"
                    >
                      <Icon name="UserPlusIcon" size={15} />
                      Invite Friends
                    </button>
                  </div>
                  <div className="flex gap-3 mt-4 flex-wrap">
                    <StatBadge value={selectedGroup.member_count ?? 0} label="Members" color="bg-muted" />
                    <StatBadge value={selectedGroup.goal_prayers_per_day} label="Daily Goal" color="bg-muted" />
                    <StatBadge value={streaks.length > 0 ? Math.max(...streaks.map((s) => s.current_streak)) : 0} label="Top Streak" color="bg-primary/10" />
                  </div>
                </div>

                {/* Streak leaderboard */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <h3 className="font-600 text-foreground mb-4 flex items-center gap-2">
                    <Icon name="FireIcon" size={18} className="text-orange-500" />
                    Streak Leaderboard
                  </h3>
                  {streaks.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      No streak data yet. Start praying together!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {streaks.map((s, idx) => (
                        <div
                          key={s.user_id}
                          className={`flex items-center gap-3 p-3 rounded-xl ${
                            idx === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-muted'
                          }`}
                        >
                          <span className="text-lg font-700 w-6 text-center text-muted-foreground">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`}
                          </span>
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-600 text-sm flex-shrink-0">
                            {s.user_profiles?.full_name?.[0]?.toUpperCase() ?? '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-600 text-foreground truncate">
                              {s.user_profiles?.full_name ?? 'Member'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {s.total_prayers} total prayers
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-orange-500">
                            <Icon name="FireIcon" size={14} />
                            <span className="text-sm font-700">{s.current_streak}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Members list */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <h3 className="font-600 text-foreground mb-4 flex items-center gap-2">
                    <Icon name="UsersIcon" size={18} className="text-primary" />
                    Members ({members.length})
                  </h3>
                  <div className="space-y-2">
                    {members.map((m) => (
                      <div key={m.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted transition-colors">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-600 text-sm flex-shrink-0">
                          {m.user_profiles?.full_name?.[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-500 text-foreground truncate">
                            {m.user_profiles?.full_name ?? 'Member'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {m.user_profiles?.email ?? ''}
                          </p>
                        </div>
                        {m.role === 'admin' && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">
                            Admin
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Notifications Tab ── */}
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-600 text-foreground">Group Notifications</h2>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline font-500"
                >
                  Mark all read
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="text-4xl mb-3">🔔</span>
                <p className="text-muted-foreground text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 p-4 rounded-2xl border transition-colors ${
                      n.is_read ? 'bg-card border-border' : 'bg-primary/5 border-primary/20'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon
                        name={(NOTIF_ICONS[n.notification_type] ?? 'BellIcon') as Parameters<typeof Icon>[0]['name']}
                        size={18}
                        className="text-primary"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-600 text-foreground">{n.title}</p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                      {n.prayer_groups?.name && (
                        <span className="inline-block mt-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          {n.prayer_groups.name}
                        </span>
                      )}
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Create Group Modal ── */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-700 text-foreground text-lg">Create Prayer Group</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-lg hover:bg-muted">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-500 text-foreground block mb-1.5">Group Name *</label>
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Family Prayer Circle"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="text-sm font-500 text-foreground block mb-1.5">Description</label>
                <textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup((p) => ({ ...p, description: e.target.value }))}
                  placeholder="What is this group about?"
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
              <div>
                <label className="text-sm font-500 text-foreground block mb-2">Group Emoji</label>
                <div className="flex flex-wrap gap-2">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      onClick={() => setNewGroup((p) => ({ ...p, avatar_emoji: e }))}
                      className={`text-xl p-2 rounded-xl border transition-all ${
                        newGroup.avatar_emoji === e
                          ? 'border-primary bg-primary/10' :'border-border hover:border-primary/40'
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-500 text-foreground block mb-1.5">
                  Daily Prayer Goal: <span className="text-primary">{newGroup.goal_prayers_per_day}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={newGroup.goal_prayers_per_day}
                  onChange={(e) => setNewGroup((p) => ({ ...p, goal_prayers_per_day: Number(e.target.value) }))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>1 prayer</span>
                  <span>5 prayers</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroup.name.trim() || saving}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite Modal ── */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-700 text-foreground text-lg">Invite Friends</h2>
              <button onClick={() => setShowInviteModal(false)} className="p-1.5 rounded-lg hover:bg-muted">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Share this invite code with friends to join <strong>{selectedGroup?.name}</strong>.
            </p>
            <div className="bg-muted rounded-xl p-4 text-center mb-4">
              <p className="text-2xl font-700 text-foreground tracking-widest">{inviteCode}</p>
              <p className="text-xs text-muted-foreground mt-1">Valid for 7 days</p>
            </div>
            <button
              onClick={handleCopyCode}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity"
            >
              <Icon name={copiedCode ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={16} />
              {copiedCode ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
        </div>
      )}

      {/* ── Join Modal ── */}
      {showJoinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-700 text-foreground text-lg">Join a Group</h2>
              <button onClick={() => { setShowJoinModal(false); setJoinCode(''); setJoinError(''); }} className="p-1.5 rounded-lg hover:bg-muted">
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the invite code shared by your group admin.
            </p>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(''); }}
              placeholder="e.g. AB12CD34"
              maxLength={8}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm text-center tracking-widest font-600 focus:outline-none focus:ring-2 focus:ring-primary/30 mb-2"
            />
            {joinError && <p className="text-xs text-destructive mb-3">{joinError}</p>}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setShowJoinModal(false); setJoinCode(''); setJoinError(''); }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinGroup}
                disabled={joinCode.length < 6 || saving}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? 'Joining…' : 'Join Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
